import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "./components/theme-provider";
import { Toaster as SonnerToaster } from "sonner";
// @deprecated — shadcn Toaster는 레거시 useToast() 호환을 위해 유지. 신규 코드는 sonner toast() 사용.
import { Toaster as ShadcnToaster } from "./components/ui/toaster";
import ProtectedRoute from "./components/common/ProtectedRoute";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { UserRole } from "./types/auth";
import { useAdminAuthStore } from "./stores/adminAuthStore";
import "./GlobalStyles.css";

/** SUPER_ADMIN + OPERATOR (ADMIN) 공유 라우트 */
const ADMIN_ROLES = [UserRole.SUPER_ADMIN, UserRole.ADMIN];
/** SUPER_ADMIN 전용 라우트 */
const SUPER_ADMIN_ONLY = [UserRole.SUPER_ADMIN];
/** 모든 Admin Portal 접근 가능 역할 (대시보드 분기용) */
const ALL_ADMIN_PORTAL = [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.ORG_ADMIN];
/** ORG_ADMIN 전용 라우트 */
const ORG_ADMIN_ONLY = [UserRole.ORG_ADMIN];

const AdminLoginPage = lazy(() => import("./pages/auth/AdminLoginPage"));
const DashboardRouter = lazy(
  () => import("./pages/dashboard/DashboardRouter"),
);
const RegistrationRequestsPage = lazy(
  () => import("./pages/registrations/RegistrationRequestsPage"),
);
const InviteCodesPage = lazy(
  () => import("./pages/invite-codes/InviteCodesPage"),
);
const OrganizationsPage = lazy(
  () => import("./pages/organizations/OrganizationsPage"),
);
const OrganizationDetailPage = lazy(
  () => import("./pages/organizations/OrganizationDetailPage"),
);
const UsersPage = lazy(() => import("./pages/users/UsersPage"));
const BusinessTypesPage = lazy(
  () => import("./pages/business-types/BusinessTypesPage"),
);
const SpecialtyProductsPage = lazy(
  () => import("./pages/specialty-products/SpecialtyProductsPage"),
);
const SubscriptionsPage = lazy(
  () => import("./pages/subscriptions/SubscriptionsPage"),
);
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage"));
const AuditLogsPage = lazy(() => import("./pages/audit-logs/AuditLogsPage"));
const AnnouncementsPage = lazy(
  () => import("./pages/announcements/AnnouncementsPage"),
);
const ExtensionsPage = lazy(() => import("./pages/extensions/ExtensionsPage"));
const IvrMenusPage = lazy(() => import("./pages/ivr/IvrMenusPage"));
const DidRoutingPage = lazy(() => import("./pages/did-routing/DidRoutingPage"));
const FaqManagementPage = lazy(() => import("./pages/faq/FaqManagementPage"));
const InquiryManagementPage = lazy(
  () => import("./pages/inquiries/InquiryManagementPage"),
);
const AdminInquiryDetailPage = lazy(
  () => import("./pages/inquiries/AdminInquiryDetailPage"),
);
const ContactInquiryManagementPage = lazy(
  () => import("./pages/contact-inquiries/ContactInquiryManagementPage"),
);
const ContactInquiryDetailPage = lazy(
  () => import("./pages/contact-inquiries/ContactInquiryDetailPage"),
);
const SipManagementPage = lazy(() => import("./pages/sip/SipManagementPage"));
const NotFoundPage = lazy(() => import("./pages/errors/NotFoundPage"));
const AiHubPage = lazy(() => import("./pages/ai-hub/AiHubPage"));
const GuidelinesLibraryPage = lazy(
  () => import("./pages/guidelines-library/GuidelinesLibraryPage"),
);
const AiMonitoringPage = lazy(
  () => import("./pages/ai-monitoring/AiMonitoringPage"),
);
const AllCallsPage = lazy(() => import("./pages/calls/AllCallsPage"));

// 통합 페이지
const TelephonyPage = lazy(() => import("./pages/telephony/TelephonyPage"));

// SUPER_ADMIN 전용 페이지
const OperatorsPage = lazy(() => import("./pages/operators/OperatorsPage"));

// ORG_ADMIN 전용 페이지
const OrgCallHistoryPage = lazy(() => import("./pages/org/CallHistoryPage"));
const OrgCallAnalyticsPage = lazy(() => import("./pages/org/CallAnalyticsPage"));
const OrgAiGuidelinesPage = lazy(() => import("./pages/org/AiGuidelinesPage"));
const OrgKnowledgeBasePage = lazy(() => import("./pages/org/KnowledgeBasePage"));
const OrgMembersPage = lazy(() => import("./pages/org/MembersPage"));
const OrgBusinessSettingsPage = lazy(() => import("./pages/org/BusinessSettingsPage"));
const OrgMySubscriptionPage = lazy(() => import("./pages/org/MySubscriptionPage"));

/**
 * Global QueryClient instance
 * Exported for use in logout handlers (cache clearing)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

/**
 * 인증된 사용자는 /dashboard, 미인증은 /login으로 리다이렉트
 */
function RoleBasedRedirect() {
  const { isAuthenticated } = useAdminAuthStore();
  return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
}

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">로딩 중...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light" storageKey="aicc-admin-ui-theme">
          <Router>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Public Route */}
                <Route path="/login" element={<AdminLoginPage />} />

                {/* ── SUPER_ADMIN + OPERATOR 공유 라우트 ── */}
                <Route path="/dashboard" element={<ProtectedRoute requiredRoles={ALL_ADMIN_PORTAL}><DashboardRouter /></ProtectedRoute>} />
                <Route path="/registrations" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><RegistrationRequestsPage /></ProtectedRoute>} />
                <Route path="/organizations" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><OrganizationsPage /></ProtectedRoute>} />
                <Route path="/organizations/:id" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><OrganizationDetailPage /></ProtectedRoute>} />
                <Route path="/business-types" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><BusinessTypesPage /></ProtectedRoute>} />
                <Route path="/specialty-products" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><SpecialtyProductsPage /></ProtectedRoute>} />
                <Route path="/subscriptions" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><SubscriptionsPage /></ProtectedRoute>} />
                <Route path="/announcements" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><AnnouncementsPage /></ProtectedRoute>} />
                <Route path="/faq" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><FaqManagementPage /></ProtectedRoute>} />
                <Route path="/inquiries" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><InquiryManagementPage /></ProtectedRoute>} />
                <Route path="/inquiries/:id" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><AdminInquiryDetailPage /></ProtectedRoute>} />
                <Route path="/contact-inquiries" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><ContactInquiryManagementPage /></ProtectedRoute>} />
                <Route path="/contact-inquiries/:id" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><ContactInquiryDetailPage /></ProtectedRoute>} />

                {/* 라우팅/인프라 — SUPER_ADMIN + OPERATOR */}
                <Route path="/telephony" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><TelephonyPage /></ProtectedRoute>} />
                <Route path="/sip-management" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><SipManagementPage /></ProtectedRoute>} />
                <Route path="/extensions" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><ExtensionsPage /></ProtectedRoute>} />
                <Route path="/ivr-menus" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><IvrMenusPage /></ProtectedRoute>} />
                <Route path="/did-routing" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><DidRoutingPage /></ProtectedRoute>} />

                {/* AI 관리 — SUPER_ADMIN + OPERATOR */}
                <Route path="/ai-hub" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><AiHubPage /></ProtectedRoute>} />
                <Route path="/guidelines-library" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><GuidelinesLibraryPage /></ProtectedRoute>} />
                <Route path="/ai-monitoring" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><AiMonitoringPage /></ProtectedRoute>} />
                <Route path="/calls" element={<ProtectedRoute requiredRoles={ADMIN_ROLES}><AllCallsPage /></ProtectedRoute>} />
                {/* 레거시 리다이렉트 (v2.0 통합으로 개별 페이지 폐지) */}
                <Route path="/ai-prompt" element={<Navigate to="/ai-hub" replace />} />
                <Route path="/ai-settings" element={<Navigate to="/ai-hub" replace />} />
                <Route path="/system-instructions" element={<Navigate to="/guidelines-library" replace />} />
                <Route path="/guidelines" element={<Navigate to="/guidelines-library" replace />} />
                <Route path="/recording-analysis-settings" element={<Navigate to="/settings" replace />} />
                <Route path="/rag-engine" element={<Navigate to="/ai-hub" replace />} />
                <Route path="/analytics" element={<Navigate to="/ai-monitoring" replace />} />

                {/* ── SUPER_ADMIN 전용 라우트 ── */}
                <Route path="/operators" element={<ProtectedRoute requiredRoles={SUPER_ADMIN_ONLY}><OperatorsPage /></ProtectedRoute>} />
                <Route path="/users" element={<ProtectedRoute requiredRoles={SUPER_ADMIN_ONLY}><UsersPage /></ProtectedRoute>} />
                <Route path="/invite-codes" element={<ProtectedRoute requiredRoles={SUPER_ADMIN_ONLY}><InviteCodesPage /></ProtectedRoute>} />
                <Route path="/audit-logs" element={<ProtectedRoute requiredRoles={SUPER_ADMIN_ONLY}><AuditLogsPage /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute requiredRoles={SUPER_ADMIN_ONLY}><SettingsPage /></ProtectedRoute>} />

                {/* ── ORG_ADMIN 전용 라우트 ── */}
                <Route path="/call-history" element={<ProtectedRoute requiredRoles={ORG_ADMIN_ONLY}><OrgCallHistoryPage /></ProtectedRoute>} />
                <Route path="/call-analytics" element={<ProtectedRoute requiredRoles={ORG_ADMIN_ONLY}><OrgCallAnalyticsPage /></ProtectedRoute>} />
                <Route path="/ai-guidelines" element={<ProtectedRoute requiredRoles={ORG_ADMIN_ONLY}><OrgAiGuidelinesPage /></ProtectedRoute>} />
                <Route path="/knowledge-base" element={<ProtectedRoute requiredRoles={ORG_ADMIN_ONLY}><OrgKnowledgeBasePage /></ProtectedRoute>} />
                <Route path="/members" element={<ProtectedRoute requiredRoles={ORG_ADMIN_ONLY}><OrgMembersPage /></ProtectedRoute>} />
                <Route path="/business-settings" element={<ProtectedRoute requiredRoles={ORG_ADMIN_ONLY}><OrgBusinessSettingsPage /></ProtectedRoute>} />
                <Route path="/my-subscription" element={<ProtectedRoute requiredRoles={ORG_ADMIN_ONLY}><OrgMySubscriptionPage /></ProtectedRoute>} />

                {/* Default Redirect — 인증 여부에 따라 분기 */}
                <Route path="/" element={<RoleBasedRedirect />} />

                {/* 404 Not Found - Catch all unmatched routes */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </Router>
          {/* @deprecated — 레거시 useToast() 호환. 신규 코드는 sonner toast() 사용 */}
          <ShadcnToaster />
          <SonnerToaster
            position="top-center"
            toastOptions={{ style: { zIndex: 99999 } }}
            richColors
          />
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
